import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../redux/userSilce";
import {
  Alert,
  Box,
  Chip,
  Container,
  Grid,
  Paper,
  Snackbar,
  Typography,
} from "@mui/material";
import useWhiteboardSocket from "./hooks/useWhiteboardSocket";
import Board from "./components/Board";
import StudentToolbar from "./components/StudentToolbar";
import AnswerModal from "./components/AnswerModal";
import Leaderboard from "./components/Leaderboard";
import MyBadgesPanel from "./components/MyBadgesPanel";
import TimerOverlay from "./components/TimerOverlay";
import CalendarOverlay from "./components/CalendarOverlay";
import WheelOverlay from "./components/WheelOverlay";
import ActivityOverlay from "./components/ActivityOverlay";
import RevealOverlay from "./components/RevealOverlay";
import YouTubeOverlay from "./components/YouTubeOverlay";
import FilesChip from "./components/FilesChip";
import LessonNotes from "./components/LessonNotes";
import AskQuestionButton from "./components/AskQuestionButton";
import axios from "axios";
import { WS_TYPES } from "./wsProtocol";
import { Button } from "@mui/material";

const BoardRoom = ({ joinData }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.user)?.token;

  const handleLogout = () => {
    axios.get(`${process.env.REACT_APP_API_URL}/auth/logout`).catch(() => { });
    dispatch(logout());
    window.location.href = "/sign-in";
  };
  const [tool, setTool] = useState({ name: "pen", color: "#1e88e5", size: 4 });
  const [starToast, setStarToast] = useState(null);
  const [gradeToast, setGradeToast] = useState(null);
  const [badgeToast, setBadgeToast] = useState(null);

  const { state, send, subscribe, boardsRef } = useWhiteboardSocket({
    wsUrl: joinData.ws_url,
    token: joinData.token,
    enabled: true,
  });

  useEffect(() => {
    if (state.lastStar) setStarToast(state.lastStar);
  }, [state.lastStar]);

  useEffect(() => {
    if (state.lastGrade) setGradeToast(state.lastGrade);
  }, [state.lastGrade]);

  useEffect(() => {
    if (state.lastBadge) setBadgeToast(state.lastBadge);
  }, [state.lastBadge]);

  // uploads authenticated by the relay join token — works for guests too
  const uploadBoardFile = (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return axios
      .post(`${process.env.REACT_APP_API_URL}/whiteboard/upload-file`, formData, {
        headers: { "X-Board-Token": joinData.token },
      })
      .then((response) => response.data.data);
  };

  const isConnected = state.status === "connected";
  const canDraw = isConnected && state.allowStudentDraw;
  const myPid = state.you?.pid;
  const myRow = state.leaderboard.find((row) => row.pid === myPid);
  const myHandRaised = state.hands.some((hand) => hand.pid === myPid);

  if (state.status === "ended") {
    return (
      <Box sx={{ background: "#F8F8FA", minHeight: "70vh", padding: "60px 0" }}>
        <Container maxWidth="sm">
          <Paper elevation={2} sx={{ borderRadius: "20px", padding: "35px", textAlign: "center" }}>
            <Typography variant="h5" mb={2}>{joinData.session.title}</Typography>
            <Typography variant="h6" color="text.secondary" mb={1}>{t("wb_session_ended")}</Typography>
            {myRow && (
              <>
                <Typography variant="h3" color="primary" mb={1}>{myRow.score}</Typography>
                <Typography color="text.secondary">
                  ⭐ {myRow.stars} — {t("wb_rank")}{" "}
                  {state.leaderboard.findIndex((row) => row.pid === myPid) + 1}
                </Typography>
              </>
            )}
            <Box mt={3} sx={{ textAlign: "start" }}>
              <MyBadgesPanel
                myRow={myRow}
                badgeCatalog={state.badgeCatalog}
                isRegistered={Boolean(token)}
              />
              <Leaderboard leaderboard={state.leaderboard} myPid={myPid} />
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ background: "#F8F8FA", minHeight: "80vh", padding: "20px 0 40px" }}>
      <Container maxWidth="xl">
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", marginBottom: "12px" }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Typography variant="h6">{joinData.session.title}</Typography>
              <LessonNotes notes={state.widgets.notes} />
            </Box>
            {joinData.owner && (
              <Typography fontSize="0.76rem" color="text.secondary">
                👨‍🏫 {joinData.owner.teacher}
                {[joinData.owner.subject, joinData.owner.class, joinData.owner.school]
                  .filter(Boolean)
                  .map((part) => ` · ${part}`)
                  .join("")}
              </Typography>
            )}
          </Box>

          {/* who am I: name + guest/registered status + sign in/out */}
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", background: "#fff", borderRadius: "100vh", padding: "4px 6px 4px 14px", boxShadow: "0 1px 6px rgba(0,0,0,0.1)" }}>
            <Typography fontWeight={700} fontSize="0.9rem">👤 {joinData.display_name}</Typography>
            <Chip
              size="small"
              color={token ? "success" : "warning"}
              label={token ? t("wb_registered") : t("wb_guest")}
              sx={{ fontWeight: 700 }}
            />
            {token ? (
              <Button
                onClick={handleLogout}
                variant="outlined"
                color="error"
                size="small"
                sx={{ borderRadius: "100vh", fontWeight: 700 }}
              >
                {t("wb_logout")}
              </Button>
            ) : (
              <Button
                component={Link}
                to="/sign-in"
                variant="contained"
                size="small"
                sx={{ borderRadius: "100vh", fontWeight: 700 }}
              >
                {t("wb_sign_in")}
              </Button>
            )}
          </Box>

          {!state.teacherOnline && isConnected && (
            <Chip color="warning" size="small" label={t("wb_teacher_offline")} />
          )}
          {state.status === "reconnecting" && (
            <Chip color="error" size="small" label={t("wb_reconnecting")} />
          )}
          {state.status === "connecting" && (
            <Chip size="small" label={t("wb_connecting")} />
          )}
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={9}>
            {canDraw && <StudentToolbar tool={tool} setTool={setTool} send={send} pdf={state.pdf} />}
            <Board
              pdf={state.pdf}
              boardsRef={boardsRef}
              subscribe={subscribe}
              send={send}
              canDraw={canDraw}
              tool={tool}
              isTeacher={false}
              textPromptLabel={t("wb_text_prompt")}
              backgrounds={state.widgets.bg}
            >
              <TimerOverlay timer={state.widgets.timer} timeOffset={state.timeOffset} isTeacher={false} />
              <CalendarOverlay calendar={state.widgets.calendar} isTeacher={false} />
              <WheelOverlay
                wheel={state.lastWheel}
                students={state.leaderboard}
                doneLabel={t("wb_wheel_winner")}
              />
              <RevealOverlay
                reveal={state.reveal}
                isTeacher={false}
                anonLabel={t("wb_anonymous")}
                labels={{
                  noFixedAnswer: t("wb_no_fixed_answer"),
                  answeredOf: t("wb_answered_of"),
                  fullMark: t("wb_full_mark"),
                  partialMark: t("wb_partial_mark"),
                  zeroMark: t("wb_zero_mark"),
                  average: t("wb_average"),
                }}
              />
              <YouTubeOverlay
                video={state.video}
                timeOffset={state.timeOffset}
                isTeacher={false}
                send={send}
                closeLabel=""
                soundLabel={t("wb_enable_sound")}
              />
              {/* teacher is preparing — hide the board content from students */}
              {state.widgets.curtain && (
                <Box sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 9,
                  background: "linear-gradient(135deg, #17202a, #24303d)",
                  color: "#fff",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1.5,
                  textAlign: "center",
                  padding: 3,
                }}>
                  <Box sx={{ fontSize: "3rem", animation: "wbCurtain 2s infinite", "@keyframes wbCurtain": { "50%": { opacity: 0.55 } } }}>🔒</Box>
                  <Typography variant="h6" fontWeight={800}>{t("wb_curtain_title")}</Typography>
                  <Typography fontSize="0.9rem" sx={{ opacity: 0.75 }}>{t("wb_curtain_sub")}</Typography>
                </Box>
              )}
              {state.shownQuestion && (
                <Box sx={{
                  position: "absolute",
                  top: 12,
                  left: "50%",
                  transform: "translateX(-50%)",
                  zIndex: 7,
                  background: "#17202a",
                  color: "#fff",
                  borderRadius: "14px",
                  padding: "10px 22px",
                  maxWidth: "88%",
                  boxShadow: "0 6px 24px rgba(0,0,0,0.4)",
                }}>
                  <Typography fontSize="0.75rem" color="#f6bf26" fontWeight={700}>
                    💬 {t("wb_question_from")} {state.shownQuestion.from}
                  </Typography>
                  <Typography fontWeight={700}>{state.shownQuestion.text}</Typography>
                </Box>
              )}
            </Board>
          </Grid>
          <Grid item xs={12} md={3}>
            <MyBadgesPanel
              myRow={myRow}
              badgeCatalog={state.badgeCatalog}
              isRegistered={Boolean(token)}
            />
            <Paper elevation={1} sx={{ borderRadius: "16px", padding: "16px" }}>
              <Typography variant="subtitle1" fontWeight={700} mb={1}>
                {t("wb_leaderboard")}
              </Typography>
              <Leaderboard leaderboard={state.leaderboard} myPid={myPid} />
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* floating raise-hand button: always visible regardless of scroll */}
      <Button
        variant="contained"
        color={myHandRaised ? "warning" : "primary"}
        disabled={!isConnected}
        onClick={() => send({ t: myHandRaised ? WS_TYPES.HAND_LOWER : WS_TYPES.HAND_RAISE })}
        sx={{
          position: "fixed",
          bottom: 24,
          insetInlineEnd: 24,
          zIndex: 30,
          borderRadius: "100vh",
          fontWeight: 800,
          fontSize: "1rem",
          padding: "12px 22px",
          boxShadow: "0 6px 24px rgba(0,0,0,0.3)",
          ...(myHandRaised && { animation: "wbHandPulse 1.2s infinite" }),
          "@keyframes wbHandPulse": {
            "50%": { transform: "scale(1.07)" },
          },
        }}
      >
        ✋ {myHandRaised ? t("wb_lower_hand") : t("wb_raise_hand")}
      </Button>

      <AnswerModal activeQuestions={state.activeQuestions} send={send} timeOffset={state.timeOffset} uploadBoardFile={uploadBoardFile} />
      <ActivityOverlay activity={state.activity} ownVote={state.ownVote} actAck={state.actAck} send={send} />
      <FilesChip sharedFiles={state.sharedFiles} uploadBoardFile={uploadBoardFile} send={send} />
      <AskQuestionButton send={send} disabled={!isConnected} />

      <Snackbar
        open={Boolean(starToast)}
        autoHideDuration={4000}
        onClose={() => setStarToast(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" icon={false} onClose={() => setStarToast(null)}>
          ⭐ {starToast && t("wb_star_awarded", { name: starToast.name })}
        </Alert>
      </Snackbar>

      {/* badge celebration — bigger than a toast, it is an achievement */}
      <Snackbar
        open={Boolean(badgeToast)}
        autoHideDuration={6000}
        onClose={() => setBadgeToast(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box
          onClick={() => setBadgeToast(null)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            background: "#17202a",
            color: "#fff",
            borderRadius: "16px",
            padding: "12px 22px",
            boxShadow: "0 8px 30px rgba(0,0,0,0.4)",
            cursor: "pointer",
            animation: "wbBadgePop 500ms ease-out",
            "@keyframes wbBadgePop": {
              from: { transform: "scale(0.85)", opacity: 0 },
              to: { transform: "scale(1)", opacity: 1 },
            },
          }}
        >
          <Box sx={{
            width: 46, height: 46, borderRadius: "50%",
            background: badgeToast?.badge?.color || "#f6bf26",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.6rem",
          }}>
            {badgeToast?.badge?.icon}
          </Box>
          <Box>
            <Typography fontSize="0.75rem" sx={{ opacity: 0.75 }}>
              🏅 {badgeToast && (badgeToast.pid === myPid ? t("wb_badge_you") : badgeToast.name)}
            </Typography>
            <Typography fontWeight={800}>{badgeToast?.badge?.name}</Typography>
            {badgeToast?.badge?.bonus > 0 && (
              <Typography fontSize="0.75rem" color="#7fd49c">+{badgeToast.badge.bonus} {t("wb_points")}</Typography>
            )}
          </Box>
        </Box>
      </Snackbar>

      <Snackbar
        open={Boolean(gradeToast)}
        autoHideDuration={4000}
        onClose={() => setGradeToast(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="info" onClose={() => setGradeToast(null)}>
          {gradeToast && t("wb_graded", { score: gradeToast.score })}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BoardRoom;
